<template>
  <div class="Password">
    <div class="Password__group">
      <el-input
                :class="['Password__field', defaultClass, disabled ? disabledClass : '']"
                :value="value"
                @input="emitValue"
                :type="inputType"
                :placeholder="placeholder"
                :required="required"
                :disabled="disabled">
        <label v-if="invalid"
               class="label-error"
               slot="prefix">
          {{ invalid }}
        </label>
        <label v-else-if="label"
               slot="prefix">{{label}}</label>
      </el-input>
      <div class="Password__icons">
        <div
          v-if="toggle"
          class="Password__toggle">
          <button
            type="button"
            class="btn-clean"
            :aria-label="showPasswordLabel"
            @click.prevent="togglePassword()">
            <i v-if="!this.$data._showPassword"
               slot="suffix" class="Password__toggle--show-password fa fa-eye-slash"
               aria-hidden="true"
               :title="$t('labels.show_password')"></i>
            <i v-else slot="suffix"
               class="Password__toggle--show-password fa fa-eye"
               aria-hidden="true"
               :title="$t('labels.hide_password')"></i>
          </button>
        </div>
      </div>
    </div>
    <div v-if="showStrengthMeter" v-bind:class="[strengthMeterClass]">
      <div v-bind:class="[strengthMeterFillClass]" :data-score="passwordStrength"></div>
    </div>
    <div class="Password__strength" :data-score="passwordStrength">
      {{ labels[passwordStrength] }}
    </div>
    <div class="Password__generate"
         @click="generatePassword">
      {{ $t('labels.automate_generate_password') }}
    </div>
  </div>
</template>

<script>
  import zxcvbn from 'zxcvbn'
  import randomPassword from 'src/services/randomPassword'

  export default {
    props: {
      /**
       * Input field id
       * @type {String}
       */
      id: {
        type: String,
        default: 'password'
      },
      /**
       * Input field placeholder text
       * @type {String}
       */
      placeholder: {
        type: String,
        default: 'Please enter your password'
      },
      /**
       * Binded value
       * @type {Object}
       */
      value: {
        default: ''
      },
      /**
       * Input field name
       * @type {String}
       */
      name: {
        type: String,
        default: 'password'
      },
      /**
       * Input field required attribute
       * @type {Boolean}
       */
      required: {
        type: Boolean,
        default: true
      },
      /**
       * Input field disabled attribute
       * @type {Boolean}
       */
      disabled: {
        type: Boolean,
        default: false
      },
      invalid: {
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      /**
       * Password min length.
       * Right now only visual for the badge
       * @type {Number}
       */
      secureLength: {
        type: Number,
        default: 7
      },
      /**
       * Display badge:
       * The badge shows your
       * password character count
       * up to the defined secureLength
       * @type {Boolean}
       */
      badge: {
        type: Boolean,
        default: true
      },
      /**
       * Show password toggle:
       * Show icon to toggle
       * the password visibility
       */
      toggle: {
        type: Boolean,
        default: true
      },
      /**
       * Prop to toggle the
       * cleartext password if
       * toggle is disabled
       */
      showPassword: {
        type: Boolean,
        default: false
      },
      /**
       * Prop to change the
       * ref of the input
       */
      referanceValue: {
        type: String,
        default: 'input'
      },
      /**
       * Prop to toggle the
       * strength Meter if
       * User wants to implement
       * their own
       */
      showStrengthMeter: {
        type: Boolean,
        default: true
      },
      /**
       * CSS Class for the Input field
       * @type {*}
       */
      defaultClass: {
        default: ''
      },
      /**
       * CSS Class for the disabled Input field
       * @type {String}
       */
      disabledClass: {
        type: String,
        default: 'Password__field--disabled'
      },
      /**
       * CSS Class for the badge
       * if a password does not match
       * the secureLength. Later for errors
       * @type {String}
       */
      errorClass: {
        type: String,
        default: 'Password__badge--error'
      },
      /**
       * CSS Class for the badge
       * if a password does match
       * the secureLength. Later for
       * success messages possible.
       * @type {String}
       */
      successClass: {
        type: String,
        default: 'Password__badge--success'
      },
      /**
       * CSS class for styling the
       * strength meter bars.
       * @type {String}
       */
      strengthMeterClass: {
        type: String,
        default: 'Password__strength-meter'
      },
      /**
       * strengthMeterFillClass sets the
       * individual strength width and fill
       * color of the strength meter bars.
       * @type {String}
       */
      strengthMeterFillClass: {
        type: String,
        default: 'Password__strength-meter--fill'
      }
    },
    data () {
      return {
        password: null,
        _showPassword: false,
        labels: {
          0: this.$t('labels.password_difficulty.vulnerable'),
          1: this.$t('labels.password_difficulty.weak'),
          2: this.$t('labels.password_difficulty.good'),
          3: this.$t('labels.password_difficulty.strong'),
          4: this.$t('labels.password_difficulty.very_good')
        }
      }
    },

    methods: {
      generatePassword () {
        this.password = randomPassword(8)
        this.$emit('show')
        this.$data._showPassword = true
        this.$emit('passwordGenerated', this.password)
      },
      /**
       * Emit passowrd value to parent component
       * @param  {String} value password typed in
       */
      emitValue (value) {
        this.password = value
        this.$emit('input', value)
      },
      togglePassword () {
        if (this.$data._showPassword) {
          this.$emit('hide')
          this.$data._showPassword = false
        } else {
          this.$emit('show')
          this.$data._showPassword = true
        }
      }
    },

    computed: {
      /**
       * passwordStrength is the score calculated by zxcvbn
       * @return {Number} Password Strength Score
       */
      passwordStrength () {
        return this.password ? zxcvbn(this.password).score : null
      },

      /**
       * isSecure checks if the length of the password is longer then
       * the defined `secureLength`
       * @return {Boolean} Password length longer then minLength
       */
      isSecure () {
        return this.password ? this.password.length >= this.secureLength : null
      },

      /**
       * isActive checks if a password is entered.
       * It's required for the password count badge.
       * @return {Boolean} Password entered
       */
      isActive () {
        return this.password && this.password.length > 0
      },

      /**
       * passwordCount holds the character count of the
       * current password. It shows the count up to the `secureLength`.
       * @return {Number} Password Character Count
       */
      passwordCount () {
        return this.password && (this.password.length > this.secureLength ? `${this.secureLength}+` : this.password.length)
      },
      /**
       * Changing the input type from password to text
       * based on the local _showPassword data or the showPassword prop
       */
      inputType () {
        return this.$data._showPassword || this.showPassword ? 'text' : 'password'
      },

      showPasswordLabel () {
        return this.$data._showPassword || this.showPassword ? 'Hide password' : 'Show password'
      }
    },

    watch: {
      value () {
        this.password = this.value
      },
      passwordStrength (score) {
        this.$emit('score', score)
        this.$emit('feedback', zxcvbn(this.password).feedback)
      }
    }
  }
</script>

<style lang="scss">
  [v-cloak] {
    display: none;
  }

  .Password {

  }

  .Password__group {
    position: relative;
  }
  .Password__generate {
    user-select: none;
    color: #2196F3;
    cursor: pointer;
  }

  .Password__strength-meter {
    position: relative;
    height: 3px;
    background: #DDD;
    margin: 10px auto 5px;
    border-radius: 3px;
  }

  .Password__strength-meter:before, .Password__strength-meter:after {
    content: '';
    height: inherit;
    background: transparent;
    display: block;
    border-color: #FFF;
    border-style: solid;
    border-width: 0 5px 0 5px;
    position: absolute;
    width: 80px;
    z-index: 10;
  }

  .Password__strength-meter:before {
    left: 70px;
  }

  .Password__strength-meter:after {
    right: 70px;
  }

  .Password__strength-meter--fill {
    background: transparent;
    height: inherit;
    position: absolute;
    width: 0;
    border-radius: inherit;
    transition: width 0.5s ease-in-out, background 0.25s;
  }

  .Password__strength-meter--fill[data-score='0'] {
    background: darkred;
    width: 20%;
  }

  .Password__strength-meter--fill[data-score='1'] {
    background: orangered;
    width: 40%;
  }

  .Password__strength-meter--fill[data-score='2'] {
    background: orange;
    width: 60%;
  }

  .Password__strength-meter--fill[data-score='3'] {
    background: yellowgreen;
    width: 80%;
  }

  .Password__strength-meter--fill[data-score='4'] {
    background: green;
    width: 100%;
  }

  .Password__strength {
    height: 20px;
  }
  .Password__strength[data-score='0'] {
    color: darkred;
  }

  .Password__strength[data-score='1'] {
    color: orangered;
  }

  .Password__strength[data-score='2'] {
    color: orange;
  }

  .Password__strength[data-score='3'] {
    color: yellowgreen;
  }

  .Password__strength[data-score='4'] {
    color: green;
  }

  .Password__field {

  }

  .Password__field--disabled {
    background-color: #f6f6f6;
    border: 1px solid #f6f6f6;
  }

  .Password__icons {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  .Password__toggle {
    line-height: 1.1;
    margin-right: 13px;
    z-index: 4;
    &--show-password {
      height: 100%;
      width: 25px;
      text-align: center;
      transition: all .3s;
      line-height: 40px;
      cursor: pointer;

      &:hover {
        color: #000;
      }
    }
  }

  .Password__badge {
    position: relative;
    color: white;
    border-radius: 6px;
    padding: 3px;
    width: 30px;
    height: 15px;
    font-size: 14px;
    line-height: 1.1;
    margin-right: 13px;
  }

  .Password__badge--error {
    background: red;
  }

  .Password__badge--success {
    background: #1bbf1b;
  }

  .btn-clean {
    appearance: none;
    background: none;
    border: none;
    cursor: pointer;
    outline: none;
    color: #777777;
    padding: 0;

    svg {
      fill: currentColor;
    }

    &:hover, &:focus {
      color: #404B69;
    }
  }

</style>